/**
 * Runtime faults (ADR 0089): the agent's own environment or tooling is broken, not the task. A
 * coding agent whose shell, git or package manager cannot work keeps failing whatever it is
 * asked, so Orgus stops starting it on that machine (agent-breaker.ts) until a self-test passes.
 *
 * Read from what each command a session ran printed (Claude Code tool results, Codex command
 * executions, ACP tool calls and the agent's stderr), never from the agent's own words:
 *
 *   - a known signature from a small catalogue (RUNTIME_SIGNATURES): AttachConsole failed, a
 *     node-pty frame, git denied its own files (EPERM, EACCES, Permission denied on .git), a
 *     sandbox refusal, a core tool (git, node, pnpm) that is not found, a shell that cannot be
 *     spawned, Windows asking which app opens a file ("Pick an app", ShellExecute);
 *   - the same tool error repeating across commands: RUNTIME_REPEAT_N of the last
 *     RUNTIME_WINDOW_M commands fail with the same normalised signature, from at least two
 *     different commands.
 *
 * A test run is the task's, never a runtime fault: a test command, or output that reads as a
 * test runner's (failing tests, assertions), is recorded but never matched. Signatures are
 * anchored at the start of a line, so a grep or cat that prints this very catalogue is not one.
 *
 * Pure and browser-safe, and serialisable: the state lives in transcript summaries. The regular
 * expressions use no lookaround or back references (the Go agent's RE2 mirrors them).
 */
import type { Ts } from '../index.ts';

/** The key under Session.ext: the fault a session's commands showed (RuntimeFault). */
export const SESSION_RUNTIME_EXT = 'runtimeFault';

export type RuntimeSignatureId =
  | 'attach-console'
  | 'node-pty'
  | 'git-metadata-denied'
  | 'sandbox-denied'
  | 'tool-missing'
  | 'not-recognized'
  | 'shell-missing'
  | 'no-file-association'
  | 'access-denied'
  /** Hub-side only (ORGU-194): repeated environment or launch failures, not a signature an agent reports. */
  | 'failure-storm';

export const RUNTIME_SIGNATURE_IDS: readonly RuntimeSignatureId[] = ['attach-console', 'node-pty', 'git-metadata-denied', 'sandbox-denied', 'tool-missing', 'not-recognized', 'shell-missing', 'no-file-association', 'access-denied'];

/** What each signature means, in one plain clause. */
export const RUNTIME_SIGNATURE_LABEL: Record<RuntimeSignatureId, string> = {
  'attach-console': 'The terminal layer failed (AttachConsole failed)',
  'node-pty': 'The terminal layer failed (node-pty)',
  'git-metadata-denied': 'Git could not write its own files',
  'sandbox-denied': 'The sandbox refused a command',
  'tool-missing': 'A core tool is not installed or not on PATH',
  'not-recognized': 'A command is not installed or not on PATH',
  'shell-missing': 'The shell could not be started',
  'no-file-association': 'Windows has no app for a command it ran (Pick an app)',
  'access-denied': 'The machine refused access to files a command needs',
  'failure-storm': 'Its environment or launch failed again and again',
};

/** Tools every coding agent needs: one that is missing is a fault at its first miss. */
export const CORE_TOOLS: readonly string[] = ['git', 'node', 'pnpm', 'npm', 'npx', 'corepack', 'bash', 'sh', 'pwsh', 'powershell', 'cmd'];

/** Same failures in the window before they count as a fault. */
export const RUNTIME_REPEAT_N = 3;
/** The commands the repetition rule looks back over. */
export const RUNTIME_WINDOW_M = 8;
/** Commands a state keeps (the self-test reads them). */
export const RUNTIME_RECENT_MAX = 16;
const OUT_HEAD_MAX = 400;
const EXCERPT_MAX = 400;
const CMD_MAX = 200;

interface SignatureDef {
  id: RuntimeSignatureId;
  re: RegExp;
  /** One occurrence is a fault; otherwise it must repeat (RUNTIME_REPEAT_N). */
  strong: boolean;
  /** Only in the output of a command that failed (or stderr): printed by a command that worked, it is text. */
  needsFailure: boolean;
}

// Every pattern is anchored at a line start: what a tool prints itself, never a quoted line.
const DENIED = String.raw`(?:Permission denied|Operation not permitted|Access is denied|Read-only file system|EPERM|EACCES)`;

export const RUNTIME_SIGNATURES: readonly SignatureDef[] = [
  { id: 'attach-console', strong: true, needsFailure: false, re: /^[ \t]*(?:\[[^\]\n]{0,40}\][ \t]*)?(?:[\w.]*Error:[ \t]*)?AttachConsole failed\b/im },
  // A stack frame inside node-pty, or an error line that names it.
  { id: 'node-pty', strong: true, needsFailure: false, re: /^[ \t]*at [^\n]*[\\/]node-pty[\w.-]*[\\/]|^[ \t]*(?:[\w.]*Error|error):[^\n]*\b(?:node-pty|conpty|winpty)\b[^\n]*\b(?:failed|error|cannot|could not)\b/im },
  {
    id: 'git-metadata-denied',
    strong: true,
    needsFailure: true,
    re: new RegExp(
      [
        String.raw`^[ \t]*(?:fatal|error|warning): [^\n]*(?:\.git[\\/]|\bindex\.lock\b|\bORIG_HEAD\b|\bHEAD\.lock\b|\.lock')[^\n]*${DENIED}`,
        String.raw`^[ \t]*(?:fatal|error): (?:Unable to create|cannot lock ref|could not lock|unable to (?:write|create|unlink))[^\n]*${DENIED}`,
        String.raw`^[ \t]*(?:[\w.]*Error: )?(?:EPERM|EACCES)[:,][^\n]*[\\/]\.git(?:[\\/]|\b)`,
      ].join('|'),
      'im',
    ),
  },
  {
    id: 'sandbox-denied',
    strong: true,
    needsFailure: true,
    re: /^[ \t]*(?:[\w.]*(?:Error|error):[ \t]*)?[^\n:]{0,30}\b(?:sandbox(?:ed)?|seatbelt|landlock)\b[^\n]{0,80}\b(?:denied|deny|denies|blocked|refused|not permitted|disallowed)\b/im,
  },
  { id: 'shell-missing', strong: true, needsFailure: true, re: /^[ \t]*(?:[\w.]*Error:[ \t]*)?spawn [^\n]*\b(?:bash|sh|zsh|cmd|powershell|pwsh)(?:\.exe)?\b[^\n]* ENOENT\b/im },
  {
    id: 'no-file-association',
    strong: true,
    needsFailure: false,
    re: /^[^\n]*\b(?:Pick an app|No application is associated with the specified file|There is no application associated|This file does not have an app associated|ShellExecute(?:Ex)?W? failed)\b/im,
  },
  // Weak: a permission refusal outside .git repeats before it counts (a locked file in a build is not a broken machine).
  { id: 'access-denied', strong: false, needsFailure: true, re: /^[ \t]*(?:[\w.]*(?:Error|error):[ \t]*)?(?:EPERM|EACCES)\b[^\n]*|^[ \t]*(?:[\w.-]*(?:Error|error)|fatal):[ \t]*[^\n]{0,80}\b(?:Access is denied|Permission denied|Operation not permitted)\b[^\n]*|^[ \t]*(?:bash|sh|zsh|cp|mv|rm|mkdir|touch|cat|ls|chmod|tar|ln):[^\n]{0,80}\b(?:Permission denied|Operation not permitted)\b[^\n]*|^[ \t]*Access is denied\.?[ \t]*$/im },
];

/** "'pnpm' is not recognized ...", "The term 'pnpm' is not recognized ...", "bash: pnpm: command not found", "zsh: command not found: pnpm". */
const MISSING_RES: readonly RegExp[] = [
  /^[ \t]*'([^'\n]{1,80})' is not recognized as an internal or external command/im,
  // PowerShell names the cmdlet or the command before " : " ("pnpm : The term ...", "Get-Command : The term ...").
  /^[ \t]*(?:[\w.-]{1,40} : )?The term '([^'\n]{1,80})' is not recognized as (?:the )?name of a cmdlet/im,
  /^[ \t]*zsh: command not found: ([\w.+-]{1,40})/im,
  /^[ \t]*(?:[^\n:]{1,60}: )?(?:line \d+: )?([\w.+-]{1,40}): (?:command )?not found\b/im,
  /^[ \t]*\/usr\/bin\/env: '?([\w.+-]{1,40})'?: No such file or directory/im,
];

/** A test command: its failures are the task's. */
const TEST_COMMAND_RE = /\b(?:pnpm|npm|yarn|bun)\s+(?:run\s+|-r\s+|--filter\s+\S+\s+)?test(?::[\w-]+)?\b|\b(?:vitest|jest|pytest|mocha|ava|tap)\b|\bplaywright\s+test\b|\b(?:go|cargo|dotnet|mvn|gradle)\s+test\b|\bnode\s+--test\b/i;
/** Output that reads as a test runner's: failing tests, assertions, a test summary. */
const TEST_OUTPUT_RE = /^[ \t]*(?:Test Files|Tests)[ \t]+[^\n]*\b(?:failed|passed)\b|\b\d+ (?:failing|passing)\b|^[ \t]*(?:FAIL|PASS)[ \t]+\S|AssertionError|\bexpected\b[^\n]{0,80}\b(?:to (?:be|equal|have)|received|but got)\b|^=+ (?:FAILURES|ERRORS|short test summary info) =+|^--- FAIL:|^not ok \d+|\bTest failed\b|^[ \t]*●[ \t]/im;
// eslint-disable-next-line no-control-regex
const ANSI_RE = /\u001b\[[0-9;?]*[A-Za-z]/g;

/** One command's result as an agent's transcript gave it. */
export interface RuntimeObservation {
  /** The command (shell), or null for the agent's own stderr. */
  command: string | null;
  output: string;
  exitCode?: number | null;
  /** The command failed (non-zero exit, the tool's error flag, a failed status). */
  failed: boolean;
  source?: 'tool' | 'stderr';
}

/** A signature found in one output. */
export interface RuntimeMatch {
  id: RuntimeSignatureId;
  strong: boolean;
  /** What makes two matches the same failure: the id, plus the tool name or the normalised line. */
  key: string;
  /** The matched line and the two after it. */
  excerpt: string;
  /** The tool that is missing (tool-missing, not-recognized). */
  tool?: string;
}

/** The evidence a fault carries (Session.ext.runtimeFault, SessionFailure.runtime). */
export interface RuntimeFault {
  signature: RuntimeSignatureId;
  /** RUNTIME_SIGNATURE_LABEL, with the missing tool named when there is one. */
  label: string;
  key: string;
  /** Occurrences seen (commands, or stderr chunks). */
  count: number;
  excerpt: string;
  /** The command that showed it, clipped; null from stderr. */
  command: string | null;
  source: 'tool' | 'stderr';
  /** It repeated across commands (a weak signature), rather than a known one seen once. */
  repeated?: boolean;
  firstAt: Ts;
  at: Ts;
}

export interface RuntimeCommand {
  cmd: string;
  failed: boolean;
  exit: number | null;
  /** The match key, or null (no signature, or a test run). */
  sig: string | null;
  /** A test command or test output: never counted. */
  task?: boolean;
  /** The head of its output (OUT_HEAD_MAX), for the self-test's checks. */
  out: string;
  at: Ts | null;
}

/** What a session's commands showed so far. Plain JSON: it travels in transcript checkpoints. */
export interface RuntimeFaultState {
  recent: RuntimeCommand[];
  fault: RuntimeFault | null;
}

export function newRuntimeState(): RuntimeFaultState {
  return { recent: [], fault: null };
}

function cleanText(text: string): string {
  return text.replace(ANSI_RE, '').replace(/\r\n?/g, '\n');
}

function excerptAt(text: string, index: number): string {
  const start = text.lastIndexOf('\n', index) + 1;
  const lines = text.slice(start).split('\n').slice(0, 3).map((l) => l.trimEnd());
  const out = lines.join('\n').trim();
  return out.length <= EXCERPT_MAX ? out : `${out.slice(0, EXCERPT_MAX - 1)}…`;
}

/** A line with paths, quoted names, numbers and hashes masked, so two runs of one failure match. */
export function normalizeRuntimeLine(line: string): string {
  return line
    .toLowerCase()
    .replace(/[a-z]:[\\/][^\s'"`]*/g, '<path>')
    .replace(/(?:\.{0,2}\/)?(?:[\w.@-]+\/){1,}[\w.@-]*/g, '<path>')
    .replace(/'[^'\n]*'|"[^"\n]*"|`[^`\n]*`/g, '<q>')
    .replace(/\b[0-9a-f]{7,}\b/g, '#')
    .replace(/\d+/g, '#')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
}

function toolName(raw: string): string {
  const base = raw.trim().replace(/^.*[\\/]/, '').toLowerCase();
  return base.replace(/\.(?:exe|cmd|bat|ps1|com)$/, '');
}

/** Is this command's output the task's (a test run)? */
export function isTaskOutput(command: string | null, output: string): boolean {
  return (!!command && TEST_COMMAND_RE.test(command)) || TEST_OUTPUT_RE.test(output);
}

/**
 * The runtime signature in one command's output, or null. A missing core tool is strong; any
 * other missing command, and a permission refusal outside .git, is weak (it must repeat).
 */
export function matchRuntimeSignature(output: string, o: { failed: boolean; exitCode?: number | null; source?: 'tool' | 'stderr' } = { failed: false }): RuntimeMatch | null {
  const text = cleanText(output);
  const failedish = o.failed || o.source === 'stderr';
  // A missing command fails the command: printed by one that worked, it is a file's text (Get-Content, grep).
  for (const re of failedish ? MISSING_RES : []) {
    const m = re.exec(text);
    if (!m?.[1]) continue;
    const tool = toolName(m[1]);
    if (!tool) continue;
    const core = CORE_TOOLS.includes(tool);
    return { id: core ? 'tool-missing' : 'not-recognized', strong: core, key: `${core ? 'tool-missing' : 'not-recognized'}:${tool}`, excerpt: excerptAt(text, m.index), tool };
  }
  for (const d of RUNTIME_SIGNATURES) {
    if (d.needsFailure && !failedish) continue;
    const m = d.re.exec(text);
    if (!m) continue;
    const key = d.strong ? d.id : `${d.id}:${normalizeRuntimeLine(m[0])}`;
    return { id: d.id, strong: d.strong, key, excerpt: excerptAt(text, m.index) };
  }
  // cmd.exe's own "not recognized" exit with nothing printed (9009): a missing command, weak.
  if (o.exitCode === 9009) return { id: 'not-recognized', strong: false, key: 'not-recognized:exit-9009', excerpt: text.trim().slice(0, EXCERPT_MAX) || 'Exit code 9009' };
  return null;
}

function labelOf(m: Pick<RuntimeMatch, 'id' | 'tool'>): string {
  if ((m.id === 'tool-missing' || m.id === 'not-recognized') && m.tool) return `${m.tool} is not installed or not on PATH`;
  return RUNTIME_SIGNATURE_LABEL[m.id];
}

function clipCmd(cmd: string): string {
  const t = cmd.replace(/\s+/g, ' ').trim();
  return t.length <= CMD_MAX ? t : `${t.slice(0, CMD_MAX - 1)}…`;
}

/**
 * Fold one command's result (or a stderr chunk) into the state. Returns the fault when this
 * observation raised it or added to it, else null. The first fault stays the evidence; later
 * matches of the same key count up.
 */
export function observeRuntime(state: RuntimeFaultState, obs: RuntimeObservation, at: Ts): RuntimeFault | null {
  const source = obs.source ?? (obs.command === null ? 'stderr' : 'tool');
  const output = obs.output ?? '';
  const task = isTaskOutput(obs.command, output);
  const m = task ? null : matchRuntimeSignature(output, { failed: obs.failed, exitCode: obs.exitCode ?? null, source });
  if (source === 'tool') {
    state.recent.push({ cmd: clipCmd(obs.command ?? ''), failed: obs.failed, exit: obs.exitCode ?? null, sig: m?.key ?? null, ...(task ? { task: true } : {}), out: cleanText(output).slice(0, OUT_HEAD_MAX), at });
    if (state.recent.length > RUNTIME_RECENT_MAX) state.recent.splice(0, state.recent.length - RUNTIME_RECENT_MAX);
  }
  if (!m) return null;
  const f = state.fault;
  if (f) {
    if (f.key !== m.key) return null;
    f.count++;
    f.at = at;
    return f;
  }
  if (m.strong) {
    state.fault = { signature: m.id, label: labelOf(m), key: m.key, count: 1, excerpt: m.excerpt, command: obs.command === null ? null : clipCmd(obs.command), source, firstAt: at, at };
    return state.fault;
  }
  // Weak: RUNTIME_REPEAT_N of the last RUNTIME_WINDOW_M commands, from two different commands.
  if (source !== 'tool') return null;
  const window = state.recent.slice(-RUNTIME_WINDOW_M).filter((c) => c.sig === m.key);
  if (window.length < RUNTIME_REPEAT_N || new Set(window.map((c) => c.cmd)).size < 2) return null;
  state.fault = { signature: m.id, label: labelOf(m), key: m.key, count: window.length, excerpt: m.excerpt, command: clipCmd(obs.command ?? ''), source, repeated: true, firstAt: window[0]!.at ?? at, at };
  return state.fault;
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** The runtime fault an agent sent (Session.ext.runtimeFault), or null when absent or malformed. */
export function sessionRuntimeFaultOf(s: { ext?: Record<string, unknown> | null } | null | undefined): RuntimeFault | null {
  const v = s?.ext?.[SESSION_RUNTIME_EXT];
  if (!isObj(v)) return null;
  if (typeof v.signature !== 'string' || !(RUNTIME_SIGNATURE_IDS as readonly string[]).includes(v.signature)) return null;
  if (typeof v.label !== 'string' || typeof v.excerpt !== 'string' || typeof v.count !== 'number' || typeof v.at !== 'number') return null;
  return v as unknown as RuntimeFault;
}

/** The fault as one plain sentence: "Git could not write its own files (3 times)." */
export function runtimeFaultSentence(f: Pick<RuntimeFault, 'label' | 'count'>): string {
  return `${f.label}${f.count > 1 ? ` (${f.count} times)` : ''}.`;
}
